import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast, Toaster } from 'sonner';

import { ApiError } from './shared/api/client';
import { routeTree } from './routeTree.gen';
import './app/styles/globals.css';

/**
 * Admin app bootstrap.  Mirrors apps/web main.tsx but with admin-specific:
 *   - MutationCache.onError shows toast for ApiError (same pattern; FE
 *     opens custom dialog for FREE_TIER_* — admin doesn't surface those,
 *     but keeping pattern symmetric for future).
 *   - No service worker / PWA — admin is desktop-only browser tool.
 */
const mutationCache = new MutationCache({
  onError: (error, _v, _c, mutation) => {
    if (mutation.meta?.silentErrors === true) return;
    toast.error(formatMutationError(error));
  },
});

function formatMutationError(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (${err.code})`;
  }
  if (err instanceof Error) return err.message;
  return 'Action failed.';
}

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {},
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster position="top-right" theme="dark" />
    </QueryClientProvider>
  </StrictMode>,
);
